/**
 * Marketing + analytics consent modal (logged-in) — P3.1.2 G-01 / G-05 / R13.4.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { CUSTOMER_FORM_DIRTY_ATTR } from '../../app/pwa/customerFormDirtyGate.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { readStoredAnalyticsSessionIds } from '../../shared/analytics/analyticsSessionStorage.js';
import { publishCustomerConsentUpdated } from '../../shared/crossTab/customerCrossTab.js';
import { Button } from '../../shared/ui/Button.js';
import { Dialog } from '../../shared/ui/Dialog.js';
import { buildAcceptSelection, type ConsentDecision } from './consentTypes.js';
import { readConsent, writeConsent } from './consentStorage.js';
import { syncAnalyticsConsentRevoke } from './consentRevokeSync.js';
import { customerLogger } from '../../shared/logging/customerLogger.js';
import { notifyConsentSyncFailed } from './consentSyncNotice.js';
import {
  useMarketingAnalyticsConsentPrompt,
  useRecordMarketingAnalyticsPromptResponse,
} from './consentApi.js';
import {
  readAnonymousKeyForMarketingSave,
  saveMarketingAnalyticsConsentPreferences,
  type SaveMarketingAnalyticsPreferencesInput,
} from './marketingAnalyticsConsentSave.js';
import { resolveConsentStorageTenantCode, PLATFORM_CONSENT_STORAGE_TENANT } from './consentStorageTenant.js';
import { resolveConsentApiFallbackTenantCode } from './resolveConsentApiFallbackTenantCode.js';
import { useCustomerAnalytics } from '../../shared/analytics/CustomerAnalyticsProvider.js';
import { useLoyaltyProfilingAvailable } from './useLoyaltyProfilingAvailable.js';
import { useCookieConsent } from './CookieConsentProvider.js';
import { useOptionalOverlayBackDismiss } from '../../shared/context/OverlayBackDismissContext.js';
import { usePlatformAccountMemberships } from '../account/accountApi.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { syncLoyaltyProfilingConsent } from './syncLoyaltyProfilingConsent.js';
import type { AnalyticsEventName } from 'pi-kiosk-shared/analyticsEvents';

export type { SaveMarketingAnalyticsPreferencesInput };
export { saveMarketingAnalyticsConsentPreferences } from './marketingAnalyticsConsentSave.js';

interface ModalFormProps {
  readonly tenantCode: string;
  readonly deferAllowed: boolean;
  readonly loyaltyProfilingAvailable: boolean;
}

function MarketingAnalyticsConsentModalForm({
  tenantCode,
  deferAllowed,
  loyaltyProfilingAvailable,
}: ModalFormProps): JSX.Element {
  const { t } = useTranslation('customer');
  const api = useApiClient();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  const { defer, complete } = useRecordMarketingAnalyticsPromptResponse();
  const analyticsManager = useCustomerAnalytics();
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;
  const storageTenant = resolveConsentStorageTenantCode(tenantCode);
  const consentOptions = { loyaltyProfilingAvailable };
  const platformMemberships = usePlatformAccountMemberships(
    session !== null && isPlatformChromeTenant(tenantCode),
  );
  const existing = readConsent(storageTenant, undefined, consentOptions);
  const [analytics, setAnalytics] = useState(existing?.analytics ?? false);
  const [marketing, setMarketing] = useState(existing?.marketing ?? false);
  const [profiling, setProfiling] = useState(existing?.profiling ?? false);
  const [saving, setSaving] = useState(false);

  const handleSave = (): void => {
    void (async (): Promise<void> => {
      setSaving(true);
      try {
        const anonymousKey = readAnonymousKeyForMarketingSave(storageTenant, analytics);
        const sessionIds = analytics
          ? []
          : [...readStoredAnalyticsSessionIds(tenantCode)];

        const cookieDecision = ((): ConsentDecision => {
          const current = readConsent(storageTenant, undefined, consentOptions);
          const profilingValue =
            loyaltyProfilingAvailable ? profiling : false;
          if (current !== null) {
            return {
              ...current,
              analytics,
              marketing,
              profiling: profilingValue,
              decidedAtMs: Date.now(),
            };
          }
          return buildAcceptSelection(
            { functional: false, analytics, marketing, profiling: profilingValue },
            Date.now(),
          );
        })();

        if (!analytics) {
          await syncAnalyticsConsentRevoke({
            tenantCode,
            api,
            decision: cookieDecision,
            anonymousKey,
            sessionIds,
            authToken: null,
            // G6 Spec A: session after `/me` enables revoke PUT (empty access OK).
            signedIn: session !== null,
            rollout: {
              api,
              fallbackTenantCode: resolveConsentApiFallbackTenantCode(
                tenantCode,
                platformMemberships.data?.memberships,
              ),
            },
          });
        } else {
          try {
            await saveMarketingAnalyticsConsentPreferences(
              {
                analytics,
                marketing,
              },
              {
                rollout: {
                  api,
                  fallbackTenantCode: resolveConsentApiFallbackTenantCode(
                    tenantCode,
                    platformMemberships.data?.memberships,
                  ),
                },
              },
            );
          } catch (err: unknown) {
            customerLogger.warn('marketing modal preferences sync failed', err);
            notifyConsentSyncFailed();
          }
        }

        if (loyaltyProfilingAvailable && session !== null) {
          try {
            await syncLoyaltyProfilingConsent({
              api,
              browseTenantCode: tenantCode,
              customerId: session.customerId,
              granted: profiling,
              memberships: platformMemberships.data?.memberships,
            });
          } catch (err: unknown) {
            customerLogger.warn('profiling consent sync failed', err);
            notifyConsentSyncFailed();
          }
        }

        writeConsent(storageTenant, cookieDecision, undefined, consentOptions);
        await complete();
        await queryClient.invalidateQueries({
          queryKey: customerQueryKeys.marketingAnalyticsConsentPrompt(
            PLATFORM_CONSENT_STORAGE_TENANT,
          ),
        });
        publishCustomerConsentUpdated(storageTenant, { scope: 'global' });
      } finally {
        setSaving(false);
      }
    })();
  };

  const handleDefer = useCallback((): void => {
    analyticsManager.track({
      eventName: 'consent_banner_dismissed' as AnalyticsEventName,
      screenName: 'marketing_analytics_consent_modal',
      metadata: {
        dismiss_source: 'defer',
      },
    });
    void (async (): Promise<void> => {
      await defer();
      await queryClient.invalidateQueries({
        queryKey: customerQueryKeys.marketingAnalyticsConsentPrompt(
          PLATFORM_CONSENT_STORAGE_TENANT,
        ),
      });
    })();
  }, [analyticsManager, defer, queryClient]);

  /** Blocking prompt: claim OverlayBackDismiss so POP + restoreLeft keep location; do not close. */
  const handleBlockingBackTrap = useCallback((): void => {
    /* no-op — restoreLeft in useHierarchicalSystemBack keeps current URL */
  }, []);

  useEffect(() => {
    if (registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(
      deferAllowed ? handleDefer : handleBlockingBackTrap,
    );
  }, [deferAllowed, handleBlockingBackTrap, handleDefer, registerOverlayDismiss]);

  const dismissOrTrap = deferAllowed ? handleDefer : handleBlockingBackTrap;

  return (
    <Dialog
      open
      onClose={dismissOrTrap}
      title={t('consent.marketingAnalyticsModal.title')}
      titleId="marketing-analytics-modal-title"
      closeLabel={t('consent.marketingAnalyticsModal.defer')}
      closeOnOverlayClick={false}
      pending={saving}
      testId="marketing-analytics-consent-modal"
      className="max-w-md"
      hideHeader
    >
      <div {...{ [CUSTOMER_FORM_DIRTY_ATTR]: 'true' }}>
      <h2
        className="text-base font-semibold text-[var(--color-on-surface)]"
        aria-hidden="true"
      >
        {t('consent.marketingAnalyticsModal.title')}
      </h2>
      <p className="mt-2 text-sm text-[var(--color-on-surface-muted)]">
        {t('consent.marketingAnalyticsModal.description')}
      </p>
      <fieldset className="mt-4 flex flex-col gap-2">
        <CategoryToggle
          id="marketing-modal-analytics"
          label={t('consent.analytics')}
          description={t('consent.analyticsDescription')}
          checked={analytics}
          onChange={setAnalytics}
        />
        <CategoryToggle
          id="marketing-modal-marketing"
          label={t('consent.marketing')}
          description={t('consent.marketingDescription')}
          checked={marketing}
          onChange={setMarketing}
        />
        {loyaltyProfilingAvailable ? (
          <CategoryToggle
            id="marketing-modal-profiling"
            label={t('loyalty.profilingTitle')}
            description={t('loyalty.profilingHelp')}
            checked={profiling}
            onChange={setProfiling}
          />
        ) : null}
      </fieldset>
      <div className="mt-4 flex flex-wrap gap-2 justify-end">
        {deferAllowed ? (
          <Button intent="ghost" size="md" onClick={handleDefer} disabled={saving}>
            {t('consent.marketingAnalyticsModal.defer')}
          </Button>
        ) : null}
        <Button intent="primary" size="md" onClick={handleSave} disabled={saving}>
          {t('consent.marketingAnalyticsModal.save')}
        </Button>
      </div>
      </div>
    </Dialog>
  );
}

interface CategoryToggleProps {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
}

function CategoryToggle({
  id,
  label,
  description,
  checked,
  onChange,
}: CategoryToggleProps): JSX.Element {
  return (
    <div className="flex items-start gap-2 text-sm">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label htmlFor={id}>
        <span className="font-medium">{label}</span>
        <span className="block text-[var(--color-on-surface-muted)]">{description}</span>
      </label>
    </div>
  );
}

export function MarketingAnalyticsConsentModal(): JSX.Element | null {
  const tenantCode = useTenantCode();
  const { session, sessionHydrated } = useCustomerSession();
  const { loyaltyProfilingAvailable } = useLoyaltyProfilingAvailable();
  const cookieConsent = useCookieConsent();
  const promptQuery = useMarketingAnalyticsConsentPrompt();

  if (!sessionHydrated || session === null) {
    return null;
  }

  // GDPR layering: cookie CMP must complete before the account M+A prompt.
  if (!cookieConsent.isConsentHydrated || cookieConsent.decision === null) {
    return null;
  }

  if (promptQuery.isLoading || promptQuery.data?.needsPrompt !== true) {
    return null;
  }

  return (
    <MarketingAnalyticsConsentModalForm
      key={`${tenantCode}-${promptQuery.dataUpdatedAt ?? 0}`}
      tenantCode={tenantCode}
      deferAllowed={promptQuery.data.deferAllowed}
      loyaltyProfilingAvailable={loyaltyProfilingAvailable}
    />
  );
}
